import type { AppDispatch, RootState } from "@store";
import { editManyEntities, updateStoryByKeys } from "@store/story";
import { getWholeEntityLayers } from "@store/story/utils";
import { getRepeatables, getSettings } from "@store/story/selectors";
import { planRepeatable } from "@/features/dynamic-variables/repeatable/plan-repeatable";
import type { KnownVariable } from "@/features/dynamic-variables/repeatable/infer-fields";
import { unwrapSettingsTokens } from "@/features/dynamic-variables/repeatable/wrap-tokens";
import { diffChangedSettings } from "@/features/dynamic-variables/repeatable/diff-settings";
import {
  findAncestorRepeatableKey,
  wouldExceedRepeatableNestingDepth,
} from "@/features/dynamic-variables/repeatable/nesting-depth";
import { reparentSettingsTokens } from "@/features/dynamic-variables/repeatable/wrap-tokens";
import { findRepeatableKeyForEntity } from "@/features/dynamic-variables/insert/list-namespace";
import {
  promptDependencyFields,
  selectFastVarDefinitions,
} from "@/features/dynamic-variables/repeatable/prompt-dependencies";
import { track } from "@/features/analytics/utils/mixpanel-actions.utils";
import { VARIABLES_EVENTS } from "@/features/dynamic-variables/analytics/variables-analytics";

export interface MakeRepeatableArgs {
  entityId: string;
  key: string;
  name: string;
  knownVariables: KnownVariable[];
  /** Author-seeded fallback rows from the "Add repeatable group" dialog's
   * predefined-data section. Defaults to none. */
  predefined?: Record<string, string>[];
}

export const makeRepeatable =
  ({ entityId, key, name, knownVariables, predefined }: MakeRepeatableArgs) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const flat = state.story.flatContentLayers;
    const subtree = getWholeEntityLayers(entityId, flat);
    if (!subtree) return;

    // Nesting-depth guard (Gap 3): the mark UI (repeatable-shortcut.tsx)
    // already disables "Make repeatable" once the ancestor chain is 3 deep,
    // but this thunk is the single choke point for every entry path, so it
    // re-checks and no-ops (with a console warning) as defense-in-depth.
    if (wouldExceedRepeatableNestingDepth(entityId, flat)) {
      console.warn(
        `[repeatable] refusing to mark "${entityId}" repeatable: ancestor chain already has the max nesting depth`,
      );
      return;
    }

    // A token belongs to its NEAREST enclosing group, so this mark takes over
    // the ancestor group's tokens that sit inside the subtree.
    const ancestorKey = findAncestorRepeatableKey(entityId, flat);
    const existingGroups = getRepeatables(state);
    const ancestorGroup = ancestorKey
      ? existingGroups.find((group) => group.key === ancestorKey)
      : undefined;

    const { repeatable, entityPatches, reparked } = planRepeatable({
      entityId,
      key,
      name,
      ancestorKey,
      ancestorFields: ancestorGroup?.fields,
      subtreeEntities: subtree.entities,
      allEntities: flat,
      knownVariables,
      predefined,
    });

    const data: Record<string, Record<string, unknown>> = {};
    for (const patch of entityPatches) data[patch.id] = patch.settings;

    // Drop each re-parked field from the ancestor group, unless the ancestor
    // still references it outside this subtree (then both keep their own).
    const droppedFromAncestor = new Set(
      reparked.filter((f) => !f.stillUsedByAncestor).map((f) => f.key),
    );
    const rebasedGroups =
      ancestorKey && droppedFromAncestor.size > 0
        ? existingGroups.map((group) =>
            group.key === ancestorKey
              ? {
                  ...group,
                  fields: group.fields.filter(
                    (field) => !droppedFromAncestor.has(field.key),
                  ),
                }
              : group,
          )
        : existingGroups;

    const fs = getSettings(state)?.fs ?? {};
    const nextFs = {
      ...fs,
      // parentKey is recorded so the share dialog can nest this group even
      // when it opens outside the editor, with no entities to derive from.
      repeatables: JSON.stringify([
        ...rebasedGroups,
        { ...repeatable, parentKey: ancestorKey ?? null },
      ]),
    };

    // Atomicity fix (Gap 2 / former KNOWN LIMITATION): the fs.repeatables
    // definition write is folded into THIS SAME edit-many undo/redo frame
    // (via fsPatch) rather than living only inside the separate
    // updateStoryByKeys dispatch below (which records no history frame on
    // its own). One Ctrl+Z now reverses the entity edits (unwrap tokens,
    // clear repeatableKey) AND the definition write together. See
    // EditManyPayloadType.fsPatch / handleUndo|RedoEditManyAction.
    dispatch(
      editManyEntities({ data, fsPatch: { current: fs, next: nextFs } }),
    );

    dispatch(
      updateStoryByKeys({
        data: { settings: { fs: nextFs } },
        autosave: true,
      }),
    );

    track(VARIABLES_EVENTS.SUCCESS_MAKE_REPEATABLE, {
      layer: flat[entityId]?.type ?? "unknown",
      "fields count": repeatable.fields.length,
      "predefined rows count": repeatable.predefined.length,
      "is nested": Boolean(ancestorKey),
    });
  };

export interface UnmarkRepeatableArgs {
  entityId: string;
}

export const unmarkRepeatable =
  ({ entityId }: UnmarkRepeatableArgs) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const flat = state.story.flatContentLayers;
    const root = flat[entityId];
    const key = root?.settings?.repeatableKey as string | undefined;
    if (!key) return;

    const subtree = getWholeEntityLayers(entityId, flat);
    if (!subtree) return;

    // Un-marking hands the tokens back to the nearest group still enclosing
    // them; only when there is none do they become bare again.
    const ancestorKey = findAncestorRepeatableKey(entityId, flat);

    const data: Record<string, Record<string, unknown>> = {};
    for (const entity of Object.values(subtree.entities)) {
      const settings = entity.settings ?? {};
      const rewritten = ancestorKey
        ? reparentSettingsTokens(settings, key, ancestorKey)
        : unwrapSettingsTokens(settings, key);
      const changed = diffChangedSettings(settings, rewritten);
      if (entity.id === entityId) changed.repeatableKey = undefined;
      if (Object.keys(changed).length > 0) data[entity.id] = changed;
    }

    const groups = getRepeatables(state);
    const removed = groups.find((r) => r.key === key);
    // The ancestor inherits the removed group's fields (its tokens now point
    // there), skipping any key it already defines.
    const existing = groups
      .filter((r) => r.key !== key)
      .map((group) => {
        // Children of the removed group move up to its own parent, so the
        // stored fallback chain never names a group that is gone.
        const reparented =
          group.parentKey === key
            ? { ...group, parentKey: removed?.parentKey ?? null }
            : group;
        if (!ancestorKey || group.key !== ancestorKey || !removed)
          return reparented;
        const own = new Set(group.fields.map((field) => field.key));
        return {
          ...reparented,
          fields: [
            ...group.fields,
            ...removed.fields.filter((field) => !own.has(field.key)),
          ],
        };
      });

    const fs = getSettings(state)?.fs ?? {};
    const nextFs = { ...fs, repeatables: JSON.stringify(existing) };

    // Atomicity fix (Gap 2 / former KNOWN LIMITATION): as in makeRepeatable,
    // fold the fs.repeatables removal into THIS SAME edit-many undo/redo
    // frame so undoing an un-mark re-wraps tokens AND restores the removed
    // definition in one Ctrl+Z.
    dispatch(
      editManyEntities({ data, fsPatch: { current: fs, next: nextFs } }),
    );

    dispatch(
      updateStoryByKeys({
        data: { settings: { fs: nextFs } },
        autosave: true,
      }),
    );
  };

export interface AddRepeatableFieldArgs {
  /** Entity the variable was inserted into (its enclosing group is resolved). */
  entityId: string;
  /** Field key — the token's name AFTER the group prefix is stripped. */
  fieldKey: string;
  /** Human label for the share-dialog column. */
  name: string;
  /** fs-variable type string, e.g. "input" | "media". */
  type: string;
}

/**
 * Registers a newly inserted variable as a FIELD of the repeatable group that
 * encloses `entityId`.
 *
 * A group's fields are derived once, at mark time, from the tokens already in
 * the subtree (deriveRepeatableFields). A variable dropped in afterwards is
 * namespaced `{{group.field}}` but would have no column in the share dialog's
 * row editor — so the sender could never fill it. This keeps the definition in
 * step with the deck. No-ops when the entity is outside any group or the field
 * already exists.
 */
export const addRepeatableField =
  ({ entityId, fieldKey, name, type }: AddRepeatableFieldArgs) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!fieldKey) return;
    const state = getState();
    const existing = getRepeatables(state);
    if (!existing.length) return;

    const flat = state.story.flatContentLayers;
    const groupKey = findRepeatableKeyForEntity(flat, entityId, existing);
    if (!groupKey) return;

    const group = existing.find((item) => item.key === groupKey);
    if (!group) return;
    if (group.fields.some((field) => field.key === fieldKey)) return;

    // An AI field brings the variables its prompt reads along as row fields
    // (each row has its own `industry`), typed from the org definitions in
    // the fast-vars cache when available.
    const added = { key: fieldKey, name, type };
    const dependencies =
      type === "ai"
        ? promptDependencyFields(
            [...group.fields, added],
            selectFastVarDefinitions(state),
            groupKey,
          )
        : [];
    const next = existing.map((item) =>
      item.key === groupKey
        ? { ...item, fields: [...item.fields, ...dependencies, added] }
        : item,
    );

    const fs = getSettings(state)?.fs ?? {};
    dispatch(
      updateStoryByKeys({
        data: {
          settings: { fs: { ...fs, repeatables: JSON.stringify(next) } },
        },
        autosave: true,
      }),
    );
  };

export interface UpdateRepeatableDefinitionArgs {
  /** Group to update. */
  key: string;
  /** Author-seeded fallback rows replacing the group's current ones
   * wholesale (e.g. from the share-dialog row editor). */
  predefined: Record<string, unknown>[];
}

/**
 * Replaces a repeatable group's `predefined` rows.
 *
 * The write carries no entity edits, so — like makeRepeatable/unmarkRepeatable
 * — it folds the settings.fs before/after snapshot into an editManyEntities
 * call with an EMPTY data map (via fsPatch) so a single Ctrl+Z reverses it,
 * then mirrors the same next-fs into updateStoryByKeys for persistence.
 * No-ops when the key names no known group.
 */
export const updateRepeatableDefinition =
  ({ key, predefined }: UpdateRepeatableDefinitionArgs) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const groups = getRepeatables(state);
    if (!groups.some((group) => group.key === key)) return;

    const next = groups.map((group) =>
      group.key === key ? { ...group, predefined } : group,
    );

    const fs = getSettings(state)?.fs ?? {};
    const nextFs = { ...fs, repeatables: JSON.stringify(next) };

    dispatch(
      editManyEntities({ data: {}, fsPatch: { current: fs, next: nextFs } }),
    );

    dispatch(
      updateStoryByKeys({
        data: { settings: { fs: nextFs } },
        autosave: true,
      }),
    );
  };
